import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { QueryOptions } from 'odata-query';
import { map, Observable, switchMap } from 'rxjs';
import {
  API_BASE_URL,
  CreateUserRoleRequest,
  DeleteUserRoleRequest,
  GstarClient,
} from '../../../web-api-client';
import {
  mapInternalUserFromResponse,
  mapInternalUserRoleFromResponse,
  mapInternalUserToCreateRequest,
  mapInternalUserToUpdateRequest,
  mapUserEngagementAssignmentFromResponse,
} from '../mappers';
import {
  mapUserCountryAssignmentFromResponse,
  toCountryLookupMap,
  UserCountryAssignmentResponse,
} from '../mappers/user-country-assignment/map-user-country-assignment-from-response';
import {
  Collection,
  InternalUser,
  InternalUserRole,
  UserCountryAssignment,
  UserEngagementAssignment,
} from '../models';
import { CountryService } from './country.service';

@Injectable({
  providedIn: 'root',
})
export class InternalUserService {
  private readonly gstarClient = inject(GstarClient);
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly countryService = inject(CountryService);

  get(
    query?: Partial<QueryOptions<InternalUser>>
  ): Observable<Collection<InternalUser>> {
    let orderBy = query?.orderBy?.toString();
    if (orderBy && orderBy.includes('fullName')) {
      orderBy = orderBy.replace('fullName', 'firstName, lastName');
    }

    return this.gstarClient
      .internalusersGET(
        undefined,
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        orderBy,
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapInternalUserFromResponse(response)
            ) ?? [],
        }))
      );
  }

  getAdmins(
    query?: Partial<QueryOptions<InternalUser>>
  ): Observable<Collection<InternalUser>> {
    let orderBy = query?.orderBy?.toString();
    if (orderBy && orderBy.includes('fullName')) {
      orderBy = orderBy.replace('fullName', 'firstName, lastName');
    }

    return this.gstarClient
      .internalusersGET(
        undefined,
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        orderBy,
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapInternalUserFromResponse(response)
            ) ?? [],
        }))
      );
  }

  getById(userId: string): Observable<InternalUser> {
    return this.gstarClient
      .internalusersGET2(userId)
      .pipe(map(response => mapInternalUserFromResponse(response)));
  }

  getUserRoles(
    userId: string,
    query?: Partial<QueryOptions<InternalUser>>
  ): Observable<InternalUserRole[]> {
    return this.gstarClient
      .userrolesGET(
        userId,
        undefined,
        undefined,
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response =>
          (response.value ?? []).map(role =>
            mapInternalUserRoleFromResponse(role)
          )
        )
      );
  }

  getCountryAssignments(userId: string): Observable<UserCountryAssignment[]> {
    return this.http
      .get<
        | UserCountryAssignmentResponse[]
        | { value?: UserCountryAssignmentResponse[] }
      >(`${this.baseUrl}/api/v1/internalusers/${encodeURIComponent(userId)}/UserCountryAssignments`)
      .pipe(
        switchMap(response => {
          const assignments = Array.isArray(response)
            ? response
            : (response.value ?? []);

          return this.countryService.get().pipe(
            map(countries => {
              const countriesById = toCountryLookupMap(countries);
              return assignments.map(assignment =>
                mapUserCountryAssignmentFromResponse(assignment, countriesById)
              );
            })
          );
        })
      );
  }

  getClientAssignments(userId: string): Observable<UserEngagementAssignment[]> {
    return this.gstarClient
      .clientassignmentsGET4(
        userId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(response =>
          (response.value ?? []).map(assignment =>
            mapUserEngagementAssignmentFromResponse(assignment)
          )
        )
      );
  }

  addToUserRole(
    userId: string,
    userRoleId: string
  ): Observable<InternalUserRole> {
    return this.gstarClient
      .userrolesPOST(
        new CreateUserRoleRequest({
          userId,
          roleId: userRoleId,
        })
      )
      .pipe(map(response => mapInternalUserRoleFromResponse(response)));
  }

  removeFromUserRole(
    userId: string,
    userRoleId: string,
    clearCountryAssignments = false
  ): Observable<void> {
    const request = new DeleteUserRoleRequest({ roleId: userRoleId });

    if (clearCountryAssignments) {
      const originalToJSON = request.toJSON.bind(request);
      request.toJSON = (data?: object): object => ({
        ...originalToJSON(data),
        clearCountryAssignments: true,
      });
    }

    return this.gstarClient.userrolesDELETE(userId, request);
  }

  addCountryAssignment(
    userId: string,
    countryId: number
  ): Observable<UserCountryAssignment> {
    return this.http
      .post<UserCountryAssignmentResponse>(
        `${this.baseUrl}/api/v1/usercountryassignments`,
        { userId, countryId }
      )
      .pipe(map(response => mapUserCountryAssignmentFromResponse(response)));
  }

  removeCountryAssignment(userCountryAssignmentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/api/v1/usercountryassignments/${userCountryAssignmentId}`
    );
  }

  create(user: InternalUser): Observable<InternalUser> {
    return this.gstarClient
      .internalusersPOST(mapInternalUserToCreateRequest(user))
      .pipe(map(response => mapInternalUserFromResponse(response)));
  }

  update(user: InternalUser): Observable<void> {
    return this.gstarClient.internalusersPUT(
      user.id,
      mapInternalUserToUpdateRequest(user)
    );
  }

  /**
   * Uses unified `POST .../users/{userId}/deleteuser` (same endpoint as external users).
   */
  deleteUser(userId: string): Observable<void> {
    return this.gstarClient.deleteuser(userId).pipe(map(() => void 0));
  }
}
